import React, { useEffect, useMemo, useRef, useState } from "react";

import Select from "antd/es/select";
import Row from "antd/lib/grid/row";
import cn from "classnames";
import { useIntl } from "react-intl";

import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import {
  IFilterComponentProps,
  IFilterInstance,
} from "@mapmycustomers/shared/types/fieldModel/IFilterConfig";
import { NumberField } from "@mapmycustomers/ui";

import IntervalUnit, { getIntervalUnitDisplayName } from "@app/enum/IntervalUnit";

import styles from "./IntervalFilter.module.scss";

export const INTERVAL_FILTER_OPERATORS = [
  FilterOperator.INTERVAL_WITHIN_LAST,
  FilterOperator.INTERVAL_WITHIN_NEXT,
  FilterOperator.INTERVAL_BEFORE,
  FilterOperator.INTERVAL_AFTER,
];

interface IntervalFilterProps extends IFilterComponentProps {}

const IntervalFilter: IFilterInstance = {
  doesSupportValue: (value: any, operator: FilterOperator) => {
    return (
      typeof value === "object" &&
      value !== null &&
      (([FilterOperator.INTERVAL_WITHIN_LAST, FilterOperator.INTERVAL_WITHIN_NEXT].includes(
        operator
      ) &&
        value.unit &&
        value.from !== undefined &&
        value.to !== undefined) ||
        ([FilterOperator.INTERVAL_AFTER, FilterOperator.INTERVAL_BEFORE].includes(operator) &&
          value.unit &&
          value.value !== undefined))
    );
  },
  getComponent:
    (): React.FunctionComponent<IntervalFilterProps> =>
    ({ className, disabled, onChange, placeholder, size = "middle", value }) => {
      const intl = useIntl();

      const [unit, setUnit] = useState<IntervalUnit>(value.value?.unit ?? IntervalUnit.DAY);
      const [count, setCount] = useState<number | undefined>();

      const previousOperatorRef = useRef<FilterOperator | undefined>(undefined);

      useEffect(() => {
        const previousOperator = previousOperatorRef.current;
        previousOperatorRef.current = value.operator;

        // When the operator changes, the incoming value.value is shaped for the
        // previous operator (or has been reset to undefined by OperatorColumn).
        // Preserve the current count so the next effect re-emits onChange with
        // the correct shape for the new operator.
        if (previousOperator !== undefined && previousOperator !== value.operator) {
          return;
        }

        setCount(
          value.operator === FilterOperator.INTERVAL_WITHIN_LAST
            ? value.value?.from !== undefined
              ? -value.value.from
              : 1
            : value.operator === FilterOperator.INTERVAL_WITHIN_NEXT
              ? (value.value?.to ?? 1)
              : value.operator === FilterOperator.INTERVAL_BEFORE
                ? value.value?.value !== undefined
                  ? -value.value.value
                  : 0
                : (value.value?.value ?? 0)
        );
      }, [value]);

      useEffect(() => {
        if (count === undefined) {
          return; // do not notify
        }

        if (
          [FilterOperator.INTERVAL_WITHIN_LAST, FilterOperator.INTERVAL_WITHIN_NEXT].includes(
            value.operator
          )
        ) {
          onChange?.({
            operator: value.operator,
            value: {
              from: value.operator === FilterOperator.INTERVAL_WITHIN_LAST ? -count : 0,
              to: value.operator === FilterOperator.INTERVAL_WITHIN_LAST ? 0 : count,
              unit,
            },
          });
        } else {
          onChange?.({
            operator: value.operator,
            value: {
              unit,
              value: value.operator === FilterOperator.INTERVAL_BEFORE ? -count : count,
            },
          });
        }
      }, [count, onChange, unit, value.operator]);

      const intervalOptions = useMemo(
        () =>
          [
            IntervalUnit.HOUR,
            IntervalUnit.DAY,
            IntervalUnit.WEEK,
            IntervalUnit.MONTH,
            IntervalUnit.YEAR,
          ].map((unit) => ({
            label: getIntervalUnitDisplayName(intl, unit, count),
            value: unit,
          })),
        [count, intl]
      );

      return (
        <Row className={cn(styles.container, className)}>
          <NumberField
            disabled={disabled}
            inputClassName={styles.unitCounter}
            min={0}
            onChange={setCount}
            placeholder={placeholder as string}
            size={size}
            value={count}
          />
          <Select<IntervalUnit>
            className={styles.unitSelector}
            disabled={disabled}
            onChange={setUnit}
            options={intervalOptions}
            size={size}
            value={unit}
          />
          {value.operator === FilterOperator.INTERVAL_BEFORE && (
            <div className={styles.suffix}>
              {intl.formatMessage({
                id: "filters.interval.suffix",
                defaultMessage: "ago",
                description: "A suffix to show for the 'was more than' operator",
              })}
            </div>
          )}
        </Row>
      );
    },
};

export default IntervalFilter;
